import type { DefinirCuidador, EstadoOnboarding } from '@pasos/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { ColumnaDelPaciente } from '../components/domain/ColumnaDelPaciente.js';
import { PantallaDeCierre } from '../components/domain/PantallaDeCierre.js';
import { Boton } from '../components/ui/Boton.js';
import { Cargando, ErrorDeCarga } from '../components/ui/Estado.js';
import { PACIENTE } from '../copy/index.js';
import { Formulario } from '../formularios/Formulario.js';
import { ErrorDeApi } from '../lib/api.js';
import { borrarSesion, guardarSesion, leerSesion } from '../lib/sesion.js';
import * as api from './api.js';
import { PasoConsentimiento } from './PasoConsentimiento.js';
import { PasoCuidador } from './PasoCuidador.js';
import { PasoIdentidad } from './PasoIdentidad.js';
import { PasoPermisos } from './PasoPermisos.js';
import { ValidacionMagicLink } from './ValidacionMagicLink.js';

/**
 * Onboarding del paciente.
 *
 * Dos etapas separadas a propósito: `Onboarding` resuelve la identidad (P-01) y
 * `FlujoOnboarding` conduce los cuatro pasos. Mientras no haya sesión, el flujo
 * ni siquiera se monta, así que no hay ninguna query pidiendo datos con un token
 * que todavía no existe.
 *
 * **La pantalla la decide el servidor**, no un estado local ni la URL. Ése es el
 * mecanismo de reanudación completo: quien abandonó en el paso 2 y vuelve tres
 * días después pide `GET /api/v1/onboarding`, recibe `pantalla: 'consentimiento'`
 * y sigue donde estaba. No hay progreso guardado en el navegador que se pierda al
 * cambiar de teléfono.
 */

/** El token llega en el fragmento (`#token=…`), que no viaja al servidor ni queda en logs. */
function tokenDeLaUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const hash = window.location.hash.replace(/^#/, '');
  const token = new URLSearchParams(hash).get('token');
  return token && token.length >= 32 ? token : null;
}

/** Se borra del historial apenas se usa: un token de acceso no se deja en la barra. */
function limpiarUrl(): void {
  if (typeof window === 'undefined') return;
  window.history.replaceState(null, '', window.location.pathname);
}

export function Onboarding() {
  const [sesionLista, setSesionLista] = useState(() => leerSesion() !== null);
  const [tokenDeAcceso] = useState(tokenDeLaUrl);

  const { mutate: validar, isError } = useMutation({
    mutationFn: (token: string) => api.validarMagicLink(token),
    onSuccess: (respuesta) => {
      // Recién ahora hay token: guardarlo es lo que dispara el envío de lo que
      // quedó en la cola (`VaciadoDeLaCola` escucha la sesión, P-132). No se
      // llama al vaciado acá: la regla vive en un solo lugar.
      guardarSesion({ idToken: respuesta.idToken, rol: respuesta.rol });
      limpiarUrl();
      setSesionLista(true);
    },
  });

  useEffect(() => {
    if (!sesionLista && tokenDeAcceso) validar(tokenDeAcceso);
  }, [sesionLista, tokenDeAcceso, validar]);

  function volverAEntrar() {
    borrarSesion();
    setSesionLista(false);
  }

  if (sesionLista) {
    return (
      <ColumnaDelPaciente>
        <FlujoOnboarding onReingreso={volverAEntrar} />
      </ColumnaDelPaciente>
    );
  }

  if (!tokenDeAcceso) {
    return <ValidacionMagicLink estado="sin-token" onReintentar={() => undefined} />;
  }

  return (
    <ValidacionMagicLink
      estado={isError ? 'error' : 'validando'}
      onReintentar={() => validar(tokenDeAcceso)}
    />
  );
}

function FlujoOnboarding({ onReingreso }: { onReingreso: () => void }) {
  const cliente = useQueryClient();

  const estado = useQuery({
    queryKey: ['onboarding'],
    queryFn: api.leerEstado,
    // Si la sesión venció no tiene sentido reintentar: hay que volver a entrar.
    retry: (intentos, error) =>
      error instanceof ErrorDeApi && error.exigeReingreso ? false : intentos < 2,
  });

  const consentimiento = useQuery({
    queryKey: ['onboarding', 'consentimiento'],
    queryFn: api.leerConsentimiento,
    enabled: estado.data?.pantalla === 'consentimiento',
  });

  // Cada mutación devuelve el estado nuevo y lo escribe en la caché: avanzar de
  // paso no necesita un refetch, y no hay ventana en la que la pantalla y el
  // servidor discrepen.
  function alAvanzar(nuevo: EstadoOnboarding) {
    cliente.setQueryData(['onboarding'], nuevo);
  }

  const identidad = useMutation({ mutationFn: api.confirmarIdentidad, onSuccess: alAvanzar });
  const firma = useMutation({ mutationFn: api.aceptarConsentimiento, onSuccess: alAvanzar });
  const cuidador = useMutation({ mutationFn: api.definirCuidador, onSuccess: alAvanzar });
  const push = useMutation({ mutationFn: api.decidirPush, onSuccess: alAvanzar });

  if (estado.isPending) {
    return (
      <div className="px-5 py-8">
        <Cargando mensaje={PACIENTE.onboarding.buscandoDondeQuedaste} />
      </div>
    );
  }

  if (estado.isError) {
    if (estado.error instanceof ErrorDeApi && estado.error.exigeReingreso) {
      return <ValidacionMagicLink estado="error" onReintentar={onReingreso} />;
    }
    return (
      <div className="px-5 py-8">
        <ErrorDeCarga onReintentar={() => void estado.refetch()} />
      </div>
    );
  }

  const datos = estado.data;

  switch (datos.pantalla) {
    case 'identidad':
      return (
        <PasoIdentidad
          enviando={identidad.isPending}
          error={identidad.isError ? PACIENTE.onboarding.identidad.errorAlGuardar : undefined}
          estado={datos}
          onConfirmar={(cirugiaAt) => identidad.mutate({ cirugiaAt })}
        />
      );

    case 'consentimiento':
      return (
        <PasoConsentimiento
          cargando={consentimiento.isPending}
          enviando={firma.isPending}
          error={firma.isError ? PACIENTE.onboarding.consentimiento.errorAlAceptar : undefined}
          errorDeCarga={consentimiento.isError}
          onAceptar={(version, textoHash) =>
            firma.mutate({ version, textoHash, leidoCompleto: true })
          }
          onReintentar={() => void consentimiento.refetch()}
          texto={consentimiento.data}
        />
      );

    case 'cuidador':
      return (
        <PasoCuidador
          enviando={cuidador.isPending}
          error={cuidador.isError ? PACIENTE.onboarding.cuidador.errorAlGuardar : undefined}
          onDefinir={(decision: DefinirCuidador) => cuidador.mutate(decision)}
        />
      );

    case 'permisos':
      return (
        <PasoPermisos
          enviando={push.isPending}
          error={push.isError ? PACIENTE.onboarding.permisos.errorAlTerminar : undefined}
          onTerminar={(habilitado) => push.mutate({ habilitado })}
        />
      );

    case 'listo':
      return <Listo />;
  }
}

/**
 * Fin del onboarding.
 *
 * El home del paciente (P-03) y su navegación llegan con el shell de paneles
 * (E3.5). Hasta entonces, la salida es el check-in: es lo que el paciente tiene
 * que poder hacer desde el día 1, y dejarlo detrás de un botón deshabilitado
 * haría que el motor de formularios no fuera alcanzable en dev.
 */
function Listo() {
  const [enCheckin, setEnCheckin] = useState(false);

  if (enCheckin) {
    // Sin hito: el check-in espontáneo. Los del plan los materializa la cadencia
    // del programa (E3.3) y llegan con su hito.
    return <Formulario formularioId="microcheck-semanal" hito={null} />;
  }

  return (
    <div className="flex min-h-dvh flex-col">
      <PantallaDeCierre
        texto={PACIENTE.onboarding.listo.detalle}
        titulo={PACIENTE.onboarding.listo.titulo}
        variante="checkin"
      />
      <div className="px-5 pt-4 pb-6">
        <Boton onClick={() => setEnCheckin(true)}>{PACIENTE.onboarding.listo.irAlCheckin}</Boton>
      </div>
    </div>
  );
}
